/**
 * ThemeTournoiV2 — Réglage de l'habillage saisonnier du site des tournois.
 *
 * On choisit un thème + une période : le site tournois.espritpadel.com applique
 * ce thème pendant la période (sinon, saisons par défaut). Donnée : clé appdata
 * `ep:tournamentTheme` = { theme, start, end } ; lue par le site via
 * l'endpoint public /api/public/tournament-theme. Écriture gated (membres Comm).
 */
import React, { useEffect, useState } from "react";

const ACCENT = "#0071CD";
const KEY = "ep:tournamentTheme";
const THEMES = [
  { v: "", label: "— Aucun (saisons par défaut) —" },
  { v: "halloween", label: "🎃 Halloween" },
  { v: "noel", label: "🎄 Noël" },
  { v: "hiver", label: "❄️ Hiver" },
  { v: "printemps", label: "🌸 Printemps" },
  { v: "ete", label: "☀️ Été" },
  { v: "terre", label: "🧱 Terre battue" },
];

// Fenêtres de saison par défaut (mois-jour) + libellé. Servent à pré-remplir les
// dates quand on choisit un thème, et à afficher la période habituelle. Côté site,
// ces mêmes fenêtres sont le repli quand aucune campagne n'est posée.
const DEFAULTS = {
  halloween: { md: ["10-20", "11-02"], label: "20 oct → 2 nov" },
  noel:      { md: ["12-01", "12-26"], label: "1er → 26 déc" },
  hiver:     { md: ["01-01", "02-28"], label: "janvier → février" },
  printemps: { md: ["03-01", "05-31"], label: "mars → mai" },
  terre:     { md: ["06-01", "06-30"], label: "juin" },
  ete:       { md: ["07-01", "08-31"], label: "juillet → août" },
};

// Calcule la prochaine occurrence de la fenêtre d'un thème (année en cours, ou
// l'an prochain si elle est déjà passée). Renvoie [start, end] en YYYY-MM-DD.
function defaultDates(theme) {
  const d = DEFAULTS[theme];
  if (!d) return ["", ""];
  const now = new Date();
  const y = now.getFullYear();
  const todayStr = now.toISOString().slice(0, 10);
  let start = `${y}-${d.md[0]}`;
  let end = `${y}-${d.md[1]}`;
  if (end < todayStr) { start = `${y + 1}-${d.md[0]}`; end = `${y + 1}-${d.md[1]}`; }
  return [start, end];
}

const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #E2E8F0",
  fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", background: "#F8FAFC", outline: "none",
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: "var(--text, #1a1a1c)", margin: "0 0 6px", display: "block" };

export default function ThemeTournoiV2() {
  const [theme, setTheme] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`/api/appdata/${encodeURIComponent(KEY)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (alive && c) { setTheme(c.theme || ""); setStart(c.start || ""); setEnd(c.end || ""); }
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  async function put(body) {
    const r = await fetch(`/api/appdata/${encodeURIComponent(KEY)}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.ok;
  }

  async function save() {
    setMsg(null);
    if (theme && (!start || !end)) { setMsg({ ok: false, t: "Choisis une date de début ET de fin." }); return; }
    if (theme && start && end && start > end) { setMsg({ ok: false, t: "La date de fin doit être après le début." }); return; }
    setSaving(true);
    const ok = await put(theme ? { theme, start, end } : null);
    setSaving(false);
    setMsg(ok ? { ok: true, t: "Enregistré ✔" } : { ok: false, t: "Échec de l'enregistrement." });
  }

  async function clearCampaign() {
    setSaving(true);
    const ok = await put(null);
    setSaving(false);
    if (ok) { setTheme(""); setStart(""); setEnd(""); setMsg({ ok: true, t: "Désactivé ✔" }); }
    else setMsg({ ok: false, t: "Échec." });
  }

  const card = {
    background: "#fff", borderRadius: 14, padding: "22px 24px",
    boxShadow: "0 2px 14px rgba(0,0,0,.05)", border: "1px solid #EEF2F7", maxWidth: 560,
  };

  return (
    <div style={{ padding: "4px 2px" }}>
      <p style={{ color: "#64748B", fontSize: 14, lineHeight: 1.5, maxWidth: 620, margin: "0 0 18px" }}>
        Choisis un thème et une période : le site des tournois l'appliquera automatiquement
        pendant cette période. En dehors, il revient aux saisons par défaut.
      </p>
      <div style={card}>
        {loading ? (
          <div style={{ color: "#94A3B8" }}>Chargement…</div>
        ) : (
          <>
            <label style={labelStyle} htmlFor="tt-theme">Thème</label>
            <select id="tt-theme" style={inputBase} value={theme}
              onChange={(e) => { const t = e.target.value; setTheme(t); const [s, en] = defaultDates(t); setStart(s); setEnd(en); setMsg(null); }}>
              {THEMES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <div style={{ display: "flex", gap: 14, marginTop: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle} htmlFor="tt-start">Début</label>
                <input id="tt-start" type="date" style={inputBase} value={start}
                  onChange={(e) => setStart(e.target.value)} disabled={!theme} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle} htmlFor="tt-end">Fin</label>
                <input id="tt-end" type="date" style={inputBase} value={end}
                  onChange={(e) => setEnd(e.target.value)} disabled={!theme} />
              </div>
            </div>
            {theme && DEFAULTS[theme] && (
              <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "#94A3B8" }}>
                Période habituelle de ce thème : <strong style={{ color: "#64748B" }}>{DEFAULTS[theme].label}</strong> (pré-remplie, ajustable).
              </p>
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 20, flexWrap: "wrap" }}>
              <button onClick={save} disabled={saving}
                style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 9, padding: "10px 20px", fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "…" : "Enregistrer"}
              </button>
              <button onClick={clearCampaign} disabled={saving}
                style={{ background: "#fff", color: "#64748B", border: "1px solid #E2E8F0", borderRadius: 9, padding: "10px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Désactiver
              </button>
              {msg && <span style={{ fontWeight: 700, fontSize: 13, color: msg.ok ? "#0a7a33" : "#C0392B" }}>{msg.t}</span>}
            </div>
          </>
        )}
      </div>
      <p style={{ marginTop: 16 }}>
        <a href="https://tournois.espritpadel.com/tournois.html" target="_blank" rel="noreferrer"
          style={{ color: ACCENT, fontSize: 13, fontWeight: 600 }}>
          Voir le site des tournois ↗
        </a>
      </p>
    </div>
  );
}
